// components/TenantLanding/Login/RegisterForms/Consent1.js
import React from "react";

const REFERRAL_SOURCE_OPTIONS = [
  { value: "", label: "Select a referral source" },
  { value: "Referral", label: "Professional Referral" },
  { value: "Mentor", label: "Mentor" },
  { value: "Employer", label: "Employer / Client" },
  { value: "Search", label: "Search Engine" },
  { value: "Social", label: "LinkedIn / Social Media" },
  { value: "Event", label: "Industry Event" },
  { value: "Other", label: "Other" },
];

const Consent1 = ({
  formData,
  errors,
  loading,
  handleChange,
  handleFileChange,
  resumeFileName,
  photoFileName,
  handlePrevious,
  isEditMode = false,
  hasExistingResumeOnFile = false, // true if server already has a resume, even without a new upload
  hasExistingPhotoOnFile = false,
}) => {
  // Resume processing consent is relevant if there's a NEW upload this session,
  // or (in edit mode) a resume already sitting on the profile.
  const resumeConsentRelevant = Boolean(formData.existing_resume) || hasExistingResumeOnFile;

  return (
    <div className="register-stage1-section">
      <h3 className="register-stage1-section__title">
        <span className="register-stage1-section__icon">✅</span>
        Consent & Referral
      </h3>
      <p className="register-stage1-section__subtitle">
        {isEditMode
          ? "Review your uploads and preferences, then resubmit for approval."
          : "Account creation authorisation and preferences."}
      </p>

      <div className="register-stage1-form__row">
        <div className="register-stage1-form__col-full">
          <div className="register-stage1-form__field-group">
            <label className="register-stage1-form__label">
              Existing Resume
              {isEditMode && hasExistingResumeOnFile && !formData.existing_resume && (
                <span className="register-stage1-form__badge register-stage1-form__badge--onfile">
                  On file
                </span>
              )}
            </label>
            <div className="register-stage1-form__file-upload">
              <input
                type="file"
                name="existing_resume"
                id="resume-upload"
                accept=".pdf,.docx"
                className="register-stage1-form__file-input"
                onChange={handleFileChange}
                disabled={loading}
              />
              <label htmlFor="resume-upload" className="register-stage1-form__file-label">
                <span className="register-stage1-form__file-icon">📄</span>
                <span>
                  {resumeFileName
                    ? resumeFileName
                    : isEditMode
                    ? "Choose PDF or DOCX file to replace"
                    : "Choose PDF or DOCX file"}
                </span>
              </label>
            </div>
            <small className="register-stage1-form__help-text">
              {isEditMode && hasExistingResumeOnFile && !formData.existing_resume
                ? "A resume is already on file. Upload a new one only if you want to replace it."
                : "PDF or DOCX. Max size limit. Imported text will never overwrite confirmed data silently."}
            </small>
          </div>
        </div>

        <div className="register-stage1-form__col-full">
          <div className="register-stage1-form__field-group">
            <label className="register-stage1-form__label">
              Profile Photograph
              {isEditMode && hasExistingPhotoOnFile && !formData.profile_photo && (
                <span className="register-stage1-form__badge register-stage1-form__badge--onfile">
                  On file
                </span>
              )}
            </label>
            <div className="register-stage1-form__file-upload">
              <input
                type="file"
                name="profile_photo"
                id="photo-upload"
                accept=".jpg,.jpeg,.png"
                className="register-stage1-form__file-input"
                onChange={handleFileChange}
                disabled={loading}
              />
              <label htmlFor="photo-upload" className="register-stage1-form__file-label">
                <span className="register-stage1-form__file-icon">🖼️</span>
                <span>
                  {photoFileName
                    ? photoFileName
                    : isEditMode
                    ? "Choose JPG or PNG to replace"
                    : "Choose JPG or PNG image"}
                </span>
              </label>
            </div>
            <small className="register-stage1-form__help-text">
              {isEditMode && hasExistingPhotoOnFile && !formData.profile_photo
                ? "A photo is already on file. Upload a new one only if you want to replace it."
                : "Optional professional photograph for profile and CV output. Requires explicit CV visibility setting."}
            </small>
          </div>
        </div>

        <div className="register-stage1-form__col-full">
          <div className="register-stage1-form__checkbox-group">
            <label className={`register-stage1-form__checkbox-wrapper ${errors.terms ? "register-stage1-form__checkbox-wrapper--error" : ""}`}>
              <input
                type="checkbox"
                name="terms"
                className="register-stage1-form__checkbox"
                checked={formData.terms}
                onChange={handleChange}
                disabled={loading}
              />
              <span className="register-stage1-form__checkbox-label">
                I accept the platform terms and conditions <span className="register-stage1-form__required">*</span>
              </span>
            </label>
            {errors.terms && (
              <span className="register-stage1-form__error-text">{errors.terms}</span>
            )}

            <label className={`register-stage1-form__checkbox-wrapper ${errors.privacy ? "register-stage1-form__checkbox-wrapper--error" : ""}`}>
              <input
                type="checkbox"
                name="privacy"
                className="register-stage1-form__checkbox"
                checked={formData.privacy}
                onChange={handleChange}
                disabled={loading}
              />
              <span className="register-stage1-form__checkbox-label">
                I acknowledge the privacy notice <span className="register-stage1-form__required">*</span>
              </span>
            </label>
            {errors.privacy && (
              <span className="register-stage1-form__error-text">{errors.privacy}</span>
            )}

            <label className={`register-stage1-form__checkbox-wrapper ${errors.resume_processing ? "register-stage1-form__checkbox-wrapper--error" : ""}`}>
              <input
                type="checkbox"
                name="resume_processing"
                className="register-stage1-form__checkbox"
                checked={formData.resume_processing}
                onChange={handleChange}
                disabled={loading || !resumeConsentRelevant}
              />
              <span className="register-stage1-form__checkbox-label">
                I permit parsing of my uploaded resume
                {resumeConsentRelevant && <span className="register-stage1-form__required">*</span>}
              </span>
            </label>
            {errors.resume_processing && (
              <span className="register-stage1-form__error-text">{errors.resume_processing}</span>
            )}
            <small className="register-stage1-form__help-text">
              Required only when a resume is uploaded{isEditMode ? " or already on file" : ""}.
            </small>

            <label className="register-stage1-form__checkbox-wrapper">
              <input
                type="checkbox"
                name="marketing"
                className="register-stage1-form__checkbox"
                checked={formData.marketing}
                onChange={handleChange}
                disabled={loading}
              />
              <span className="register-stage1-form__checkbox-label">
                I would like to receive platform updates and communications
              </span>
            </label>
          </div>
        </div>

        <div className="register-stage1-form__col-half">
          <div className="register-stage1-form__field-group">
            <label className="register-stage1-form__label">How did you find Qualion?</label>
            <select
              name="referral_source"
              className="register-stage1-form__select"
              value={formData.referral_source}
              onChange={handleChange}
              disabled={loading}
            >
              {REFERRAL_SOURCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="register-stage1-form__col-half">
          <div className="register-stage1-form__field-group">
            <label className="register-stage1-form__label">Referral or Invitation Code</label>
            <input
              type="text"
              name="referral_code"
              className="register-stage1-form__input"
              value={formData.referral_code}
              onChange={handleChange}
              placeholder="Enter code if you have one"
              disabled={loading}
            />
            <small className="register-stage1-form__help-text">
              Links to campaign, mentor or company.
            </small>
          </div>
        </div>
      </div>

      <div className="register-stage1-form__actions">
        <button
          type="button"
          className="register-stage1-form__btn register-stage1-form__btn--outline"
          onClick={handlePrevious}
          disabled={loading}
        >
          ← Back
        </button>
        <button
          type="submit"
          className="register-stage1-form__btn register-stage1-form__btn--primary register-stage1-form__btn--submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="register-stage1-form__btn-spinner"></span>
              {isEditMode ? "Resubmitting..." : "Creating Account..."}
            </>
          ) : isEditMode ? (
            "Resubmit Application"
          ) : (
            "Create Account"
          )}
        </button>
      </div>
    </div>
  );
};

export default Consent1;